"use client";

import { Box, Text } from "@chakra-ui/react";
import Image from "next/image";
import { useThemeTokens } from "@/lib/theme/tokens";
import { getTenantDisplayName } from "@/lib/utils/tenant";
import type { GetTenantResponse } from "@/openapi/generated";

interface TenantLogoProps {
  width: number;
  height: number;
  tenantData?: GetTenantResponse | null;
  showPlaceholder?: boolean; // If true, shows placeholder even if logo exists (for development)
  src?: string | null; // Optional logo URL (e.g. uploaded tenant logo)
  alt?: string;
  priority?: boolean;
  style?: React.CSSProperties;
  onError?: () => void;
}

/**
 * TenantLogo - tenant logo, or a dashed placeholder when none is available.
 * `width` / `height` are max bounds; the image keeps its aspect ratio.
 */
export const TenantLogo = ({
  width,
  height,
  tenantData,
  showPlaceholder = false,
  src,
  alt,
  priority = false,
  style,
  onError,
}: TenantLogoProps) => {
  const theme = useThemeTokens();
  const tenantName = getTenantDisplayName(tenantData);

  const hasLogo = src || null;
  const shouldShowPlaceholder = showPlaceholder || !hasLogo;

  if (shouldShowPlaceholder) {
    return (
      <Box
        width={"50px"}
        height={"43px"}
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={
          theme.mode === "dark"
            ? "rgba(255,255,255,0.05)"
            : "rgba(11,13,16,0.05)"
        }
        borderWidth="1px"
        borderColor={theme.borderGlass.default}
        borderRadius="4px"
        borderStyle="dashed"
        flexShrink={0}
        style={style}
      >
        <Text
          fontSize="xs"
          fontWeight="500"
          color={theme.text.tertiary}
          textTransform="uppercase"
          letterSpacing="0.05em"
        >
          Logo
        </Text>
      </Box>
    );
  }

  if (!src) {
    return null;
  }

  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      w="auto"
      h="auto"
      maxW={`${width}px`}
      maxH={`${height}px`}
      style={style}
    >
      <Image
        src={hasLogo}
        alt={alt || tenantName}
        width={width}
        height={height}
        style={{
          width: "auto",
          height: "auto",
          maxWidth: `${width}px`,
          maxHeight: `${height}px`,
          objectFit: "contain",
          display: "block",
        }}
        priority={priority}
        onError={onError}
        aria-hidden={alt ? undefined : true}
      />
    </Box>
  );
};
